import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { APP_KIND } from "@config";
import { getV2Identity } from "../auth/v2Auth";
import { clearV2Cache } from "../api/v2Cache";
import {
  fetchClubSessions,
  fetchInternalLoad,
  fetchInternalLoadTrends,
} from "./v2OverviewApi";
import {
  latestSessionForDate,
  mapAvailability,
  mapInternalPlayers,
  parseSessionId,
} from "./v2OverviewMapper";

export default function useTeamOverviewInternalLoad(clubId) {
  const { t, i18n } = useTranslation();
  const resolvedClubId = clubId || getV2Identity().club_id;
  const [sessionId, setSessionId] = useState(null);
  const [trendPeriod, setTrendPeriod] = useState("week");
  const [sessions, setSessions] = useState([]);
  const [sessionDates, setSessionDates] = useState([]);
  const [latestSessionId, setLatestSessionId] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [trends, setTrends] = useState(null);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [trendsLoading, setTrendsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const { date, period } = parseSessionId(sessionId);
  const latestDate = parseSessionId(latestSessionId).date;

  const loadSessions = useCallback(async () => {
    if (!resolvedClubId) {
      setSessionsLoading(false);
      setSnapshotLoading(false);
      setError("missing_club");
      return;
    }
    setSessionsLoading(true);
    setError(null);
    try {
      const catalog = await fetchClubSessions(resolvedClubId);
      const nextSessions = catalog?.data || [];
      const nextLatest = catalog?.meta?.latest_session_id || nextSessions.at(-1)?.id || null;
      const dates = catalog?.meta?.session_dates?.length
        ? catalog.meta.session_dates.map((item) => String(item).slice(0, 10))
        : [...new Set(nextSessions.map((item) => item.date))];
      setSessions(nextSessions);
      setSessionDates(dates);
      setLatestSessionId(nextLatest);
      setSessionId((current) => (
        nextSessions.some((item) => item.id === current) ? current : nextLatest
      ));
      if (!nextLatest) {
        setSnapshot(null);
        setTrends(null);
        setSnapshotLoading(false);
      }
    } catch (cause) {
      setError(cause);
      setSnapshotLoading(false);
    } finally {
      setSessionsLoading(false);
    }
  }, [resolvedClubId]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    if (!resolvedClubId || !sessionId) {
      setSnapshotLoading(false);
      setTrendsLoading(false);
      return undefined;
    }
    let active = true;
    setSnapshotLoading(true);
    const selected = parseSessionId(sessionId);
    fetchInternalLoad(resolvedClubId, { date: selected.date, period: selected.period })
      .then((loadSnapshot) => {
        if (!active) return;
        setSnapshot(loadSnapshot);
        setError(null);
      })
      .catch((cause) => {
        if (active) setError(cause);
      })
      .finally(() => {
        if (active) setSnapshotLoading(false);
      });
    return () => {
      active = false;
    };
  }, [refreshNonce, resolvedClubId, sessionId]);

  useEffect(() => {
    if (!resolvedClubId || !sessionId) {
      setTrendsLoading(false);
      return undefined;
    }
    let active = true;
    setTrendsLoading(true);
    const selected = parseSessionId(sessionId);
    fetchInternalLoadTrends(resolvedClubId, {
      period: trendPeriod,
      date: selected.date,
    })
      .then((trendPayload) => {
        if (!active) return;
        setTrends(trendPayload);
      })
      .catch(() => {
        if (active) setTrends(null);
      })
      .finally(() => {
        if (active) setTrendsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [refreshNonce, resolvedClubId, sessionId, trendPeriod]);

  const daySessions = useMemo(
    () => sessions.filter((item) => item.date === date),
    [date, sessions],
  );

  const selectDate = useCallback((iso) => {
    const next = latestSessionForDate(sessions, iso);
    if (next) setSessionId(next.id);
  }, [sessions]);

  const players = useMemo(
    () => mapInternalPlayers(snapshot?.players || []),
    [snapshot],
  );
  const availability = useMemo(
    () => mapAvailability(snapshot?.availability, t, i18n.language),
    [i18n.language, snapshot, t],
  );

  return {
    clubId: resolvedClubId,
    sessions,
    daySessions,
    sessionDates,
    sessionId,
    setSessionId,
    selectDate,
    latestSessionId,
    latestDate,
    session: snapshot?.session || sessions.find((item) => item.id === sessionId) || null,
    availability,
    players,
    trends,
    trendPeriod,
    setTrendPeriod,
    loading: sessionsLoading || snapshotLoading || Boolean(resolvedClubId && sessionId && !snapshot && !error),
    sessionsLoading,
    snapshotLoading,
    trendsLoading,
    error,
    reload: () => {
      if (resolvedClubId) {
        clearV2Cache(`/${APP_KIND}/clubs/${resolvedClubId}`);
      }
      setRefreshNonce((value) => value + 1);
      loadSessions();
    },
    date,
    period,
  };
}
